import { useState } from 'react';
import { FileText, X, Sparkles } from 'lucide-react';
import { generateMoM } from '../lib/api';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../hooks/useToast';
import { Link } from 'wouter';

interface WrapUpPromptProps {
  sessionId: string;
  onDismiss: () => void;
  onMoMGenerated: () => void;
}

const WrapUpPrompt = ({ sessionId, onDismiss, onMoMGenerated }: WrapUpPromptProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { getToken } = useAuth();
  const { success, error } = useToast();

  const handleGenerateMoM = async () => {
    try {
      setIsGenerating(true);
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');

      await generateMoM(sessionId, token);
      success('MoM generated successfully!');
      onMoMGenerated();
    } catch (err: any) {
      if (err.message?.includes('already been generated')) {
        success('MoM already exists for this session');
        onMoMGenerated();
      } else {
        error('Failed to generate MoM');
        console.error('Error generating MoM:', err);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-orange-800 mb-1">Ready for your MoM?</p>
            <p className="text-xs text-orange-700">Generate a summary of key takeaways and action items from this session</p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 text-orange-400 hover:text-orange-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleGenerateMoM}
          disabled={isGenerating}
          className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50 transition-colors"
        >
          {isGenerating ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          Generate MoM
        </button>
        <button
          onClick={onDismiss}
          className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50 transition-colors"
        >
          Later
        </button>
        <Link
          href="/archive"
          className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50 transition-colors"
        >
          View Archive
        </Link>
      </div>
    </div>
  );
};

export default WrapUpPrompt;
